/* Blog admin panel — extracted from Admin.jsx tech-debt split. */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import useAiJob from "@/hooks/useAiJob";
import {
  Sparkle, PencilSimple, Trash, EyeSlash, Eye, UploadSimple, X as XIcon, Robot, PushPin, PushPinSlash,
  CalendarBlank, Lock, LockOpen, ArrowsClockwise, SkipForward,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MAX_UPLOAD_MB = 10;
const ACCEPT_MEDIA = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime";

/* ------------------------------------------------------------------ */
/* Editorial Brief — plan the next 4 Sunday AI-blog runs               */
/* ------------------------------------------------------------------ */
function EditorialBrief() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/blog/brief");
      setData(data);
    } catch { toast.error("Failed to load editorial brief"); }
  };
  useEffect(() => { load(); }, []);

  const humanDate = (iso) => {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  };

  const cycleAngle = async (week) => {
    setBusy(`cycle-${week}`);
    try { await api.post(`/admin/blog/brief/${week}/cycle-angle`); await load(); toast.success("Angle cycled — new pick shown"); }
    catch { toast.error("Cycle failed"); }
    finally { setBusy(""); }
  };
  const toggleSkip = async (week, skip) => {
    setBusy(`skip-${week}`);
    try {
      await api.post(`/admin/blog/brief/${week}`, { skip: !skip });
      await load();
      toast.success(!skip ? "Week marked as skip" : "Skip cleared");
    } catch { toast.error("Update failed"); }
    finally { setBusy(""); }
  };
  const lockEvent = async (week, ev) => {
    setBusy(`lock-${week}`);
    try {
      await api.post(`/admin/blog/brief/${week}`, { locked_event: ev });
      await load();
      toast.success(`Locked: ${ev.name}`);
    } catch { toast.error("Lock failed"); }
    finally { setBusy(""); }
  };
  const unlock = async (week) => {
    setBusy(`unlock-${week}`);
    try {
      await api.post(`/admin/blog/brief/${week}`, { locked_event: null });
      await load();
      toast.success("Unlocked — auto-pick will decide");
    } catch { toast.error("Unlock failed"); }
    finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <div className="card-recipe mb-6 bg-rf-cream-alt/60 border-rf-ink/10" data-testid="editorial-brief-panel">
      <div className="flex items-start gap-3 mb-4">
        <CalendarBlank size={28} weight="duotone" className="text-rf-orange mt-1" />
        <div>
          <p className="font-display font-black text-lg text-rf-ink">Editorial Brief — next 4 Sunday runs</p>
          <p className="text-sm text-rf-ink-soft">
            Each Sunday at 06:00 UTC the AI drafts one post about an event 21–45 days out.
            Lock a topic, skip a week, or cycle to a different angle before it runs.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {data.weeks.map((w) => {
          const pick = w.locked_event
            ? { kind: "event", event: w.locked_event, locked: true }
            : w.auto_pick;
          const name = pick.kind === "event" ? pick.event.name : pick.track.name;
          const region = pick.kind === "event" ? pick.event.region : "editorial";
          const days = pick.kind === "event" ? pick.event.days_away : null;
          const isSkip = w.skip;
          return (
            <div
              key={w.week_start_iso}
              className={`rounded-xl border p-4 ${isSkip ? "bg-rf-ink/5 border-rf-ink/20 opacity-70" : "bg-white border-rf-ink/10"}`}
              data-testid={`brief-week-${w.week_start_iso}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="overline">{humanDate(w.week_start_iso)} · 06:00 UTC</span>
                {w.locked_event && (
                  <span className="chip !bg-rf-orange !text-white"><Lock size={12} weight="bold" /> Locked</span>
                )}
                {isSkip && <span className="chip !bg-rf-ink/10 !text-rf-ink">Skipped</span>}
              </div>
              <p className="font-display font-black text-base text-rf-ink leading-tight">{name}</p>
              <p className="text-xs text-rf-ink-soft mt-1">
                {region.toUpperCase()}{days !== null ? ` · ${days} days out` : ""}
                {pick.kind === "event" && pick.event.keywords?.length ? ` · SEO: ${pick.event.keywords.join(", ")}` : ""}
              </p>
              {pick.kind === "event" && pick.event.angle_hint && (
                <p className="text-xs text-rf-ink-soft italic mt-2">“{pick.event.angle_hint}”</p>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-3">
                {!w.locked_event ? (
                  <button
                    data-testid={`brief-cycle-${w.week_start_iso}`}
                    onClick={() => cycleAngle(w.week_start_iso)}
                    disabled={busy === `cycle-${w.week_start_iso}`}
                    className="btn-outline-ink !py-1.5 !px-3 !text-xs"
                  >
                    <ArrowsClockwise size={13} /> Different angle
                  </button>
                ) : (
                  <button
                    data-testid={`brief-unlock-${w.week_start_iso}`}
                    onClick={() => unlock(w.week_start_iso)}
                    disabled={busy === `unlock-${w.week_start_iso}`}
                    className="btn-outline-ink !py-1.5 !px-3 !text-xs"
                  >
                    <LockOpen size={13} /> Unlock
                  </button>
                )}
                <button
                  data-testid={`brief-skip-${w.week_start_iso}`}
                  onClick={() => toggleSkip(w.week_start_iso, isSkip)}
                  disabled={busy === `skip-${w.week_start_iso}`}
                  className="btn-outline-ink !py-1.5 !px-3 !text-xs"
                >
                  <SkipForward size={13} /> {isSkip ? "Un-skip" : "Skip this week"}
                </button>
              </div>

              {/* Alternate lockable events */}
              {!w.locked_event && w.upcoming_options?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-rf-ink-soft cursor-pointer hover:text-rf-orange">
                    Lock a different event ({w.upcoming_options.length} nearby)
                  </summary>
                  <div className="mt-2 flex flex-col gap-1">
                    {w.upcoming_options.map((ev) => (
                      <button
                        key={ev.date_iso + ev.name}
                        onClick={() => lockEvent(w.week_start_iso, ev)}
                        className="text-left text-xs px-2 py-1.5 rounded hover:bg-rf-amber/20 border border-transparent hover:border-rf-amber/40"
                      >
                        <span className="font-bold">{ev.name}</span>
                        <span className="text-rf-ink-soft"> · {ev.days_away}d · {ev.region.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {data.avoid?.titles?.length > 0 && (
        <details className="mt-4">
          <summary className="overline cursor-pointer">
            Recent coverage the AI must not repeat ({data.avoid.titles.length} titles / {data.avoid.keywords.length} keywords)
          </summary>
          <div className="mt-2 text-xs text-rf-ink-soft space-y-1">
            <p><span className="font-bold">Titles:</span> {data.avoid.titles.slice(0, 10).join(" · ")}</p>
            <p><span className="font-bold">Keywords:</span> {data.avoid.keywords.slice(0, 15).join(", ")}</p>
          </div>
        </details>
      )}
    </div>
  );
}

export default function BlogPanel() {
  const [blog, setBlog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draftingSeo, setDraftingSeo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [promptText, setPromptText] = useState("");
  const fileRef = useRef(null);

  const refresh = async () => {
    try { const { data } = await api.get("/admin/blog"); setBlog(data); } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const generateJob = useAiJob({
    statusUrl: (id) => `/admin/blog/ai-generate/status/${id}`,
    onDone: (job) => {
      // The final Blog result is nested in job.result (async) OR is the whole
      // payload (sync path). Support both shapes.
      const payload = job.result || job;
      const author = payload?.author ? ` (drafted by ${payload.author})` : "";
      const title = payload?.title || "new draft";
      toast.success(`Draft created${author}: ${title} — review and publish below`);
      setGenerating(false);
      refresh();
    },
    onCancelled: (job) => {
      const note = job?.result?.note || "Cancelled.";
      toast(note);
      setGenerating(false);
      refresh();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || e?.message || "AI generation failed");
      setGenerating(false);
    },
  });

  const aiGenerate = async (usePrompt = false) => {
    setGenerating(true);
    try {
      await generateJob.start(async () => {
        const body = usePrompt && promptText.trim() ? { prompt: promptText.trim() } : {};
        const { data } = await api.post("/admin/blog/ai-generate", body);
        if (usePrompt) setPromptText("");
        return data;
      });
    } catch {
      toast.error("AI generation failed");
      setGenerating(false);
    }
  };

  const togglePublished = async (post) => {
    await api.patch(`/admin/blog/${post.post_id}`, { published: !post.published });
    toast.success(post.published ? "Suspended" : "Published");
    refresh();
  };

  const togglePin = async (post) => {
    // Max 2 pinned at any time. The backend gracefully auto-unpins the oldest
    // pinned post if the admin tries to pin a 3rd — so this call can never
    // fail under that constraint.
    try {
      await api.patch(`/admin/blog/${post.post_id}`, { pinned: !post.pinned });
      toast.success(post.pinned ? `"${post.title}" unpinned` : `"${post.title}" pinned to the top (max 2)`);
      refresh();
    } catch { toast.error("Pin update failed"); }
  };

  const deletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    await api.delete(`/admin/blog/${post.post_id}`);
    toast.success("Deleted");
    refresh();
  };

  const saveEdit = async () => {
    try {
      // If the tags field was mid-edit and never blurred, parse the raw text now.
      const rawTags = editing._tagsRaw;
      const finalTags = typeof rawTags === "string"
        ? rawTags.split(",").map((s) => s.trim()).filter(Boolean)
        : (editing.tags || []);
      await api.patch(`/admin/blog/${editing.post_id}`, {
        title: editing.title, h1: editing.h1, h2: editing.h2,
        // author_name — MUST be in the payload or the "Recipe Filer" reset
        // (and any other admin choice) is silently dropped and the UI reverts.
        author_name: editing.author_name || "Recipe Filer",
        seo_summary: editing.seo_summary, body: editing.body,
        slug: editing.slug, primary_keyword: editing.primary_keyword || "",
        tags: finalTags, images: editing.images || [],
        published: editing.published,
        pinned: !!editing.pinned,
        // Full SEO overrides — precede seo_summary when populated.
        seo_title: editing.seo_title || "",
        seo_description: editing.seo_description || "",
        seo_keywords: editing.seo_keywords || "",
        image_alt: editing.image_alt || "",
      });
      toast.success("Post saved");
      setEditing(null);
      refresh();
    } catch { toast.error("Save failed"); }
  };

  const draftSeoAI = async () => {
    if (!editing?.post_id) {
      toast.error("Save the post first, then draft SEO");
      return;
    }
    // Confirm before we overwrite anything the admin has already written.
    const hasExisting =
      (editing.seo_title && editing.seo_title.trim())
      || (editing.seo_description && editing.seo_description.trim())
      || (editing.image_alt && editing.image_alt.trim())
      || (editing.seo_keywords && editing.seo_keywords.trim());
    if (hasExisting && !window.confirm("Overwrite the SEO fields you've already filled in?")) return;
    setDraftingSeo(true);
    try {
      const { data } = await api.post(`/admin/blog/${editing.post_id}/suggest-seo`);
      if (!data || (!data.seo_title && !data.seo_description && !data.image_alt && !data.seo_keywords)) {
        toast.error("AI didn't return anything usable — try again in a moment");
        return;
      }
      setEditing({
        ...editing,
        seo_title: data.seo_title || editing.seo_title || "",
        seo_description: data.seo_description || editing.seo_description || "",
        seo_keywords: data.seo_keywords || editing.seo_keywords || "",
        image_alt: data.image_alt || editing.image_alt || "",
      });
      toast.success("SEO drafted — review then Save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setDraftingSeo(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file || !editing) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`File is too large — please keep uploads under ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing({ ...editing, images: [...(editing.images || []), data.url] });
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };

  return (
    <div>
      <EditorialBrief />

      <div className="card-recipe mb-6 bg-rf-amber/10 border-rf-amber/30">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Robot size={32} weight="duotone" className="text-rf-orange" />
            <div>
              <p className="font-display font-black text-xl text-rf-ink">AI Recipe Blog Generator</p>
              <p className="text-sm text-rf-ink-soft">Auto-drafts every Sunday at 06:00 UTC so posts are ready to edit Monday morning. Manual triggers below.</p>
            </div>
          </div>
          <button data-testid="admin-ai-blog-generate" onClick={() => aiGenerate(false)} disabled={generating} className="btn-amber"><Sparkle size={18} /> {generating ? "Generating…" : "Generate auto-rotation draft"}</button>
        </div>
        <div className="border-t border-rf-amber/30 pt-4">
          <label className="overline mb-2 block">Or write to a topic prompt</label>
          <div className="flex flex-wrap gap-2">
            <input
              data-testid="admin-ai-blog-prompt"
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="e.g. The rise of sourdough baking, autumn pickling traditions, Diwali sweet recipes…"
              className="input-rf flex-1 min-w-[260px]"
              onKeyDown={(e) => { if (e.key === "Enter" && promptText.trim() && !generating) aiGenerate(true); }}
            />
            <button
              data-testid="admin-ai-blog-generate-prompt"
              onClick={() => aiGenerate(true)}
              disabled={generating || !promptText.trim()}
              className="btn-outline-ink"
            >
              <Sparkle size={18} /> {generating ? "Generating…" : "Draft from prompt"}
            </button>
          </div>
          <p className="text-xs text-rf-ink-soft mt-2">Drafts unpublished — review the copy + hyperlinks, then publish from the list below.</p>
        </div>
        {generateJob.progress && (
          <div className="mt-4 border-t border-rf-amber/30 pt-4" data-testid="admin-ai-blog-progress">
            <div className="flex items-center justify-between text-xs font-bold text-rf-ink-soft mb-2">
              <span>
                {generateJob.progress.status === "done"
                  ? "Draft ready ✓"
                  : generateJob.progress.status === "cancelled"
                  ? "Cancelled"
                  : generateJob.progress.status === "error"
                  ? "Failed"
                  : "Drafting — Claude is writing, Nano Banana is illustrating. This usually takes 60-90 seconds."}
              </span>
              {generateJob.progress.status === "running" && !generateJob.progress.cancel_requested && (
                <button
                  type="button"
                  onClick={generateJob.cancel}
                  data-testid="admin-ai-blog-cancel"
                  className="text-rf-ink-soft hover:text-red-600 underline font-semibold"
                >
                  Cancel
                </button>
              )}
              {generateJob.progress.cancel_requested && generateJob.progress.status === "running" && (
                <span className="text-amber-600 font-semibold">Cancelling…</span>
              )}
            </div>
            <div className="w-full h-2 rounded-full bg-rf-ink/10 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  generateJob.progress.status === "cancelled" ? "bg-rf-ink-soft" :
                  generateJob.progress.status === "error" ? "bg-red-500" :
                  "bg-rf-orange"
                } ${generateJob.progress.status === "running" ? "animate-pulse" : ""}`}
                style={{
                  width: generateJob.progress.status === "running"
                    ? "70%"
                    : generateJob.progress.status === "done"
                    ? "100%"
                    : "100%",
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {blog.map((p) => (
          <div key={p.post_id} className="card-recipe relative">
            <div className="flex items-start gap-3 mb-3 flex-wrap">
              {p.pinned && <span className="chip !bg-rf-orange !text-white font-bold"><PushPin size={12} weight="fill" /> Pinned</span>}
              {p.auto_generated && <span className="chip"><Robot size={12} weight="bold" /> AI</span>}
              {p.published ? (
                <span className="chip !bg-green-100 !text-green-800">Published</span>
              ) : (
                <span className="chip !bg-rf-ink/10 !text-rf-ink">Suspended</span>
              )}
              {p.primary_keyword && <span className="chip !bg-rf-cream-alt !text-rf-ink-soft">SEO: {p.primary_keyword}</span>}
            </div>
            {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full aspect-[16/9] object-cover rounded-xl mb-3" />}
            <h3 className="font-display font-extrabold text-lg text-rf-ink mb-1 line-clamp-2">{p.title}</h3>
            <p className="text-xs text-rf-ink-soft mb-3">/{p.slug} · by {p.author_name}</p>
            <p className="text-sm text-rf-ink-soft line-clamp-2 mb-4">{p.seo_summary}</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid={`blog-edit-${p.post_id}`} onClick={() => setEditing({ ...p })} className="btn-outline-ink !py-2 !px-3 !text-xs"><PencilSimple size={14} /> Edit</button>
              <button data-testid={`blog-toggle-${p.post_id}`} onClick={() => togglePublished(p)} className="btn-outline-ink !py-2 !px-3 !text-xs">
                {p.published ? <><EyeSlash size={14} /> Suspend</> : <><Eye size={14} /> Publish</>}
              </button>
              <button
                data-testid={`blog-pin-${p.post_id}`}
                onClick={() => togglePin(p)}
                className={`!py-2 !px-3 !text-xs inline-flex items-center gap-1 rounded-full font-bold transition-colors ${p.pinned ? "bg-rf-orange text-white hover:bg-rf-orange/80" : "border border-rf-ink/15 bg-white text-rf-ink hover:bg-rf-amber/30"}`}
                title="At most 2 posts can be pinned at any time. Pinning a 3rd auto-unpins the oldest pinned post."
              >
                {p.pinned ? <><PushPinSlash size={14} /> Unpin</> : <><PushPin size={14} /> Pin</>}
              </button>
              <Link to={`/news/${p.slug}`} target="_blank" className="btn-outline-ink !py-2 !px-3 !text-xs">View →</Link>
              <button onClick={() => deletePost(p)} className="text-red-600 inline-flex items-center gap-1 text-xs font-bold ml-auto"><Trash size={14} /> Delete</button>
            </div>
          </div>
        ))}
        {blog.length === 0 && <p className="text-rf-ink-soft text-center col-span-full py-10">No posts yet — generate one with AI or create manually via the API.</p>}
      </div>

      {/* Blog edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-rf-ink">Edit blog post</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="overline">Title</label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="input-rf mt-1 !font-display !font-black" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="overline">Slug</label>
                  <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="input-rf mt-1 font-mono text-sm" />
                </div>
                <div>
                  <label className="overline">SEO primary keyword</label>
                  <input value={editing.primary_keyword || ""} onChange={(e) => setEditing({ ...editing, primary_keyword: e.target.value })} className="input-rf mt-1" />
                </div>
              </div>
              <div>
                <label className="overline">H1</label>
                <input value={editing.h1 || ""} onChange={(e) => setEditing({ ...editing, h1: e.target.value })} className="input-rf mt-1" />
              </div>
              <div>
                <label className="overline">H2 (subhead)</label>
                <input value={editing.h2 || ""} onChange={(e) => setEditing({ ...editing, h2: e.target.value })} className="input-rf mt-1" />
              </div>
              <div>
                <label className="overline">SEO summary (meta description)</label>
                <textarea rows={2} value={editing.seo_summary || ""} onChange={(e) => setEditing({ ...editing, seo_summary: e.target.value })} className="input-rf mt-1" />
              </div>

              {/* Advanced SEO overrides — when set, take precedence over
                  the "SEO summary" above on the public /news/{slug} meta
                  tags + JSON-LD. Use the "Draft with AI" button in Bulk
                  SEO panel to auto-fill these. */}
              <details className="bg-rf-cream-alt/40 rounded-2xl p-4" open>
                <summary className="cursor-pointer font-bold text-sm text-rf-ink flex items-center justify-between gap-3">
                  <span>Advanced SEO overrides</span>
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); draftSeoAI(); }}
                      disabled={draftingSeo}
                      data-testid="blog-edit-draft-seo-ai"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rf-orange text-white font-bold text-[11px] uppercase tracking-wider hover:bg-rf-ink disabled:opacity-50"
                      title="Ask Claude to draft an SEO title, description, image alt, and keywords for this post"
                    >
                      <Sparkle size={12} weight="fill" />
                      {draftingSeo ? "Drafting…" : "Draft SEO with AI"}
                    </button>
                    <span className="text-xs text-rf-ink-soft font-normal">(auto-generated if left blank)</span>
                  </span>
                </summary>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="overline">SEO title override</label>
                    <input
                      value={editing.seo_title || ""}
                      onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })}
                      placeholder={editing.title}
                      maxLength={70}
                      className="input-rf mt-1"
                      data-testid="blog-edit-seo-title"
                    />
                    <p className="text-[10px] text-rf-ink-soft mt-1">{(editing.seo_title || "").length} / 70 · Google truncates around 60.</p>
                  </div>
                  <div>
                    <label className="overline">Image alt (hero)</label>
                    <input
                      value={editing.image_alt || ""}
                      onChange={(e) => setEditing({ ...editing, image_alt: e.target.value })}
                      placeholder="Describe the hero photo…"
                      maxLength={140}
                      className="input-rf mt-1"
                      data-testid="blog-edit-image-alt"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline">SEO description override</label>
                    <textarea
                      rows={2}
                      value={editing.seo_description || ""}
                      onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })}
                      placeholder={editing.seo_summary || "Overrides the SEO summary when set."}
                      maxLength={200}
                      className="input-rf mt-1"
                      data-testid="blog-edit-seo-description"
                    />
                    <p className="text-[10px] text-rf-ink-soft mt-1">{(editing.seo_description || "").length} / 200 · Google truncates around 160.</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline">SEO keywords (comma-separated)</label>
                    <input
                      value={editing.seo_keywords || ""}
                      onChange={(e) => setEditing({ ...editing, seo_keywords: e.target.value })}
                      placeholder="how to boil an egg, egg cookery, breakfast tips"
                      className="input-rf mt-1"
                      data-testid="blog-edit-seo-keywords"
                    />
                  </div>
                </div>
              </details>
              <div>
                <label className="overline">Body (use [link text](https://url) for hyperlinks)</label>
                <textarea rows={12} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="input-rf mt-1 font-mono text-sm" />
                <p className="text-xs text-rf-ink-soft mt-1">Tip: wrap hyperlinks like <code className="font-mono bg-rf-cream-alt px-1 rounded">[Yorkshire Gold tea](https://example.com)</code> — they&apos;ll render as clickable links that open in a new tab. Use a blank line between paragraphs.</p>
              </div>
              <div>
                <label className="overline">Author</label>
                <select
                  value={editing.author_name || "Maisie Sullivan"}
                  onChange={(e) => setEditing({ ...editing, author_name: e.target.value })}
                  className="input-rf mt-1"
                  data-testid="blog-edit-author"
                >
                  <option>Maisie Sullivan</option>
                  <option>Rivers Wild</option>
                  <option>Mrs B</option>
                  <option>Recipe Filer</option>
                </select>
                <p className="text-xs text-rf-ink-soft mt-1">Auto-rotates each week (Maisie → Rivers → Mrs B) for AI drafts. Edit manually if a guest writes.</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="overline">Images</label>
                  <button onClick={() => fileRef.current?.click()} className="text-rf-orange text-xs font-bold inline-flex items-center gap-1"><UploadSimple size={14} /> Upload</button>
                  <input ref={fileRef} type="file" accept={ACCEPT_MEDIA} className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(editing.images || []).map((url, i) => (
                    <div key={i} className="relative aspect-video rounded-lg overflow-hidden group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, idx) => idx !== i) })} className="absolute top-1 right-1 bg-rf-ink/80 text-rf-cream rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100"><XIcon size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="overline">Tags (comma separated)</label>
                <input
                  data-testid="blog-tags-input"
                  type="text"
                  defaultValue={(editing.tags || []).join(", ")}
                  key={editing.post_id || "new"}
                  onChange={(e) => { editing._tagsRaw = e.target.value; }}
                  onBlur={(e) => setEditing({
                    ...editing,
                    tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })}
                  className="input-rf mt-1"
                  placeholder="e.g. cooking tips, family recipes, comfort food"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-5 h-5 accent-rf-orange" />
                <span className="font-display font-bold text-rf-ink">Published (untick to suspend)</span>
              </label>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setEditing(null)} data-testid="blog-edit-cancel" className="btn-outline-ink">Cancel</button>
                <button onClick={saveEdit} data-testid="blog-edit-save" className="btn-amber">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
